/**
 * 预览组件状态管理
 * 统一维护预览弹窗中用户配置的参数，使其生命周期不受弹窗开关影响
 */

const STORAGE_KEY_PREVIEW_PARAMS = 'nges_preview_params_persist';
const STORAGE_KEY_COMPONENT_PARAMS = 'nges_preview_component_params_persist';

/**
 * 从 localStorage 读取持久化的参数数据
 */
function loadPersistedData(storageKey) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw) return JSON.parse(raw);
  } catch (e) {
    // 解析失败时忽略
  }
  return null;
}

/**
 * 将参数数据写入 localStorage
 */
function savePersistedData(storageKey, data) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(data));
  } catch (e) {
    // 写入失败时忽略
  }
}

/**
 * 清除 localStorage 中的持久化数据
 */
function removePersistedData(storageKey) {
  try {
    localStorage.removeItem(storageKey);
  } catch (e) {
    // 清除失败时忽略
  }
}

// 初始化时从 localStorage 恢复持久化标记
const savedPreviewParams = loadPersistedData(STORAGE_KEY_PREVIEW_PARAMS);
const savedComponentParams = loadPersistedData(STORAGE_KEY_COMPONENT_PARAMS);

export default {
  namespaced: true,
  state: {
    // 预览配置状态（所有参数统一维护在此对象中）
    previewState: {
      // 用户手动选择的布局类型覆盖值（null 表示使用自动检测值）
      layoutTypeOverride: null,
      // 级联选择器当前选中的自定义路径
      selectedPath: [],
      // URL 参数列表（如果有持久化数据则恢复）
      previewParams: savedPreviewParams?.previewParams || [],
      // 组件 Params 参数列表（如果有持久化数据则恢复）
      componentParams: savedComponentParams?.componentParams || [],
      // URL 参数输入模式：'kv' 键值对模式，'text' 文本输入模式
      previewParamsMode: savedPreviewParams?.previewParamsMode || 'kv',
      // 组件 Params 参数输入模式：'kv' 键值对模式，'text' 文本输入模式
      componentParamsMode: savedComponentParams?.componentParamsMode || 'kv',
      // 文本模式下的 URL 参数原始文本
      previewParamsText: savedPreviewParams?.previewParamsText || '',
      // 文本模式下的组件 Params 参数原始文本
      componentParamsText: savedComponentParams?.componentParamsText || '',
      // URL 参数是否开启本地持久化
      previewParamsPersist: !!savedPreviewParams,
      // 组件 Params 参数是否开启本地持久化
      componentParamsPersist: !!savedComponentParams,
    },
  },
  getters: {
    getPreviewState(state) {
      return state.previewState;
    },
  },
  mutations: {
    // 更新预览状态中的指定字段
    updatePreviewState(state, payload) {
      state.previewState = { ...state.previewState, ...payload };

      // 如果 URL 参数开启了持久化，同步到 localStorage
      if (state.previewState.previewParamsPersist) {
        savePersistedData(STORAGE_KEY_PREVIEW_PARAMS, {
          previewParams: state.previewState.previewParams,
          previewParamsMode: state.previewState.previewParamsMode,
          previewParamsText: state.previewState.previewParamsText,
        });
      }

      // 如果组件 Params 开启了持久化，同步到 localStorage
      if (state.previewState.componentParamsPersist) {
        savePersistedData(STORAGE_KEY_COMPONENT_PARAMS, {
          componentParams: state.previewState.componentParams,
          componentParamsMode: state.previewState.componentParamsMode,
          componentParamsText: state.previewState.componentParamsText,
        });
      }
    },
    // 切换 URL 参数持久化开关
    togglePreviewParamsPersist(state, enabled) {
      state.previewState = { ...state.previewState, previewParamsPersist: enabled };
      if (enabled) {
        // 开启：将当前参数写入 localStorage
        savePersistedData(STORAGE_KEY_PREVIEW_PARAMS, {
          previewParams: state.previewState.previewParams,
          previewParamsMode: state.previewState.previewParamsMode,
          previewParamsText: state.previewState.previewParamsText,
        });
      } else {
        // 关闭：清除 localStorage
        removePersistedData(STORAGE_KEY_PREVIEW_PARAMS);
      }
    },
    // 切换组件 Params 持久化开关
    toggleComponentParamsPersist(state, enabled) {
      state.previewState = { ...state.previewState, componentParamsPersist: enabled };
      if (enabled) {
        savePersistedData(STORAGE_KEY_COMPONENT_PARAMS, {
          componentParams: state.previewState.componentParams,
          componentParamsMode: state.previewState.componentParamsMode,
          componentParamsText: state.previewState.componentParamsText,
        });
      } else {
        removePersistedData(STORAGE_KEY_COMPONENT_PARAMS);
      }
    },
    // 重置所有预览状态（切换编辑对象时调用）
    // 开启了持久化的参数不会被清除，而是从 localStorage 恢复
    resetPreviewState(state) {
      const persistedPreview = state.previewState.previewParamsPersist
        ? loadPersistedData(STORAGE_KEY_PREVIEW_PARAMS)
        : null;
      const persistedComponent = state.previewState.componentParamsPersist
        ? loadPersistedData(STORAGE_KEY_COMPONENT_PARAMS)
        : null;

      state.previewState = {
        layoutTypeOverride: null,
        selectedPath: [],
        // URL 参数：持久化时从 localStorage 恢复，否则清空
        previewParams: persistedPreview?.previewParams || [],
        previewParamsMode: persistedPreview?.previewParamsMode || 'kv',
        previewParamsText: persistedPreview?.previewParamsText || '',
        previewParamsPersist: !!persistedPreview,
        // 组件 Params：持久化时从 localStorage 恢复，否则清空
        componentParams: persistedComponent?.componentParams || [],
        componentParamsMode: persistedComponent?.componentParamsMode || 'kv',
        componentParamsText: persistedComponent?.componentParamsText || '',
        componentParamsPersist: !!persistedComponent,
      };
    },
  },
  actions: {},
};
